import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Snackbar, Chip, Stack
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function GestionBodegas() {
  const [bodegas, setBodegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: '', codigo: '', direccion: '', responsable: '', telefono: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/bodegas`, { headers: headers() });
      setBodegas(res.data?.data || []);
    } catch { setBodegas([]); }
    setLoading(false);
  };

  const abrirDialogo = (bodega = null) => {
    if (bodega) {
      setEditando(bodega);
      setForm({ nombre: bodega.nombre, codigo: bodega.codigo, direccion: bodega.direccion || '', responsable: bodega.responsable || '', telefono: bodega.telefono || '' });
    } else {
      setEditando(null);
      setForm({ nombre: '', codigo: '', direccion: '', responsable: '', telefono: '' });
    }
    setDialogOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre) return setSnackbar({ open: true, message: 'El nombre es requerido', severity: 'error' });
    try {
      if (editando) {
        await axios.put(`${API_URL}/api/bodegas/${editando.id}`, form, { headers: headers() });
        setSnackbar({ open: true, message: 'Bodega actualizada', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/api/bodegas`, form, { headers: headers() });
        setSnackbar({ open: true, message: 'Bodega creada', severity: 'success' });
      }
      setDialogOpen(false);
      cargar();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  const eliminar = async (bodega) => {
    if (bodega.es_principal) return setSnackbar({ open: true, message: 'No se puede eliminar la bodega principal', severity: 'warning' });
    if (!window.confirm(`¿Eliminar bodega "${bodega.nombre}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/bodegas/${bodega.id}`, { headers: headers() });
      setSnackbar({ open: true, message: 'Bodega eliminada', severity: 'success' });
      cargar();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={700}>🏭 Gestión de Bodegas</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => abrirDialogo()}>Nueva Bodega</Button>
          </Stack>
        </Box>

        {loading ? <Typography>Cargando...</Typography> : bodegas.length === 0 ? (
          <Alert severity="info">No hay bodegas registradas.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Código</strong></TableCell>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Dirección</strong></TableCell>
                  <TableCell><strong>Responsable</strong></TableCell>
                  <TableCell><strong>Teléfono</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bodegas.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell><Typography fontFamily="monospace">{b.codigo}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600}>{b.nombre}</Typography>
                        {b.es_principal && <Chip label="Principal" size="small" color="primary" />}
                      </Box>
                    </TableCell>
                    <TableCell>{b.direccion || '—'}</TableCell>
                    <TableCell>{b.responsable || '—'}</TableCell>
                    <TableCell>{b.telefono || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label={b.activa ? 'Activa' : 'Inactiva'} size="small" color={b.activa ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirDialogo(b)}><Edit /></IconButton></Tooltip>
                      {!b.es_principal && (
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminar(b)}><Delete /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editando ? '✏️ Editar Bodega' : '➕ Nueva Bodega'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth required label="Nombre" value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              <TextField fullWidth label="Código" value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                helperText="Se genera auto si se deja vacío" />
            </Stack>
            <TextField fullWidth label="Dirección" value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Responsable" value={form.responsable}
                onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
              <TextField fullWidth label="Teléfono" value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>{editando ? 'Actualizar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
